import { Button, Card, useToast } from '@citizen-walk/shared-ui';
import {
  DEFAULT_POST_ANALYSIS_KEY,
  isDefaultPostAnalysis,
  listPostAnalysisOptions,
  type District,
} from '@citizen-walk/shared-types';
import { httpsCallable } from 'firebase/functions';
import { useState } from 'react';
import { functions } from '../../lib/firebase';

/**
 * forceReanalyze callable 結線パネル (Sprint BC、GAP-F5)。
 *
 * master role が指定 post の AI 解析を強制再実行する admin UI。
 *
 * scope (minimum):
 *   - postIds を「カンマ / 改行区切り」で入力 → 解析
 *   - 結果一覧 (postId / status / error 個別表示)
 *
 * 別 sprint:
 *   - 条件指定 (mode / status 一括) は別 sprint で本 callable に追加 input (criteria)
 *     を実装したタイミングで UI 拡張する。
 *
 * 認可: master のみ (callable 側で `requireMaster`)。UI でも guard。
 */

interface ForceReanalyzeResultItem {
  postId: string;
  ok: boolean;
  status?: 'pending' | 'processing' | 'done' | 'failed' | 'skipped';
  error?: string;
}
interface ForceReanalyzeResult {
  districtId: string;
  total: number;
  successCount: number;
  failedCount: number;
  items: ForceReanalyzeResultItem[];
}

const MAX_POST_IDS = 50;

/** pure: 入力テキストを postId 配列に正規化 (test 容易化)。 */
export function parsePostIdsInput(text: string): string[] {
  return text
    .split(/[\s,]+/)
    .map((s) => s.trim())
    .filter((s) => s.length > 0);
}

export interface DistrictForceReanalyzePanelProps {
  district: District;
  isMaster: boolean;
}

export function DistrictForceReanalyzePanel({
  district,
  isMaster,
}: DistrictForceReanalyzePanelProps) {
  const { toast } = useToast();
  const [postIdsText, setPostIdsText] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<ForceReanalyzeResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  // docs/29: 再解析する観点 (既定の観点 / 追加の観点)。追加の観点が無い地区では選択を出さない。
  const analysisOptions = listPostAnalysisOptions(district.aiAnalysis);
  const [analysisKey, setAnalysisKey] = useState<string>(DEFAULT_POST_ANALYSIS_KEY);

  const postIds = parsePostIdsInput(postIdsText);
  const overLimit = postIds.length > MAX_POST_IDS;

  async function handleRun() {
    if (!isMaster || postIds.length === 0 || overLimit) return;
    setBusy(true);
    setResult(null);
    setError(null);
    try {
      const callable = httpsCallable<
        { districtId: string; postIds: string[]; surveyKey?: string },
        ForceReanalyzeResult
      >(functions, 'forceReanalyze');
      const res = await callable({
        districtId: district.id,
        postIds,
        ...(isDefaultPostAnalysis(analysisKey) ? {} : { surveyKey: analysisKey }),
      });
      setResult(res.data);
      toast(
        res.data.failedCount === 0 ? 'success' : 'info',
        `再解析完了: 成功 ${res.data.successCount} / 失敗 ${res.data.failedCount} (全 ${res.data.total} 件)`,
      );
    } catch (e) {
      const msg = (e as Error).message;
      setError(msg);
      toast('error', `forceReanalyze 失敗: ${msg}`);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card padding="lg" className="border-primary-200">
      <h3 className="text-base font-bold text-slate-900 mb-1">
        🔄 AI 強制再解析（マスター管理者のみ）
      </h3>
      <p className="text-xs text-slate-500 mb-3">
        指定した投稿のAI解析をやり直します (1 件ずつ Gemini を呼出、最大 {MAX_POST_IDS} 件 / 1 分に
        1 回まで)。
      </p>

      {!isMaster && <p className="text-xs text-warning-700 mb-2">master role のみ実行可能です。</p>}

      <label className="grid grid-cols-1 gap-1 mb-3">
        <span className="text-xs text-slate-500">
          postIds (カンマ / 改行 / 空白区切り、最大 {MAX_POST_IDS} 件)
        </span>
        <textarea
          value={postIdsText}
          onChange={(e) => setPostIdsText(e.target.value)}
          disabled={!isMaster || busy}
          rows={4}
          placeholder="post-001, post-002, ..."
          className="min-h-[6em] px-3 py-2 border border-slate-300 rounded text-sm font-mono"
        />
        <span className="text-[11px] text-slate-400">
          現在 {postIds.length} 件
          {overLimit && (
            <span className="text-danger-700 ml-2">※ {MAX_POST_IDS} 件を超えています</span>
          )}
        </span>
      </label>

      {analysisOptions.length > 1 && (
        <label className="block text-xs text-slate-500 mb-3 min-w-0">
          再解析する観点
          <select
            className="mt-0.5 block rounded border border-slate-300 px-2 py-1 text-sm bg-white"
            style={{ maxWidth: '100%' }}
            value={analysisKey}
            disabled={!isMaster || busy}
            onChange={(e) => setAnalysisKey(e.target.value)}
          >
            {analysisOptions.map((o) => (
              <option key={o.key} value={o.key}>
                {o.label}
                {o.isDefault ? '（既定）' : ''}
              </option>
            ))}
          </select>
        </label>
      )}

      <div className="flex justify-end mb-3">
        <Button
          onClick={() => void handleRun()}
          disabled={!isMaster || busy || postIds.length === 0 || overLimit}
        >
          {busy ? '再解析中…' : `再解析実行 (${postIds.length} 件)`}
        </Button>
      </div>

      {error && (
        <p className="text-sm text-danger-700" role="alert">
          {error}
        </p>
      )}

      {result && (
        <section>
          <p className="text-sm mb-2">
            完了: 成功 <span className="text-success-700 font-semibold">{result.successCount}</span>{' '}
            / 失敗 <span className="text-danger-700 font-semibold">{result.failedCount}</span> / 全{' '}
            {result.total}
          </p>
          <ul className="text-xs grid grid-cols-1 gap-1 max-h-60 overflow-auto">
            {result.items.map((item) => (
              <li
                key={item.postId}
                className={`px-2 py-1 rounded border ${
                  item.ok
                    ? 'border-success-200 bg-success-50 text-slate-700'
                    : 'border-danger-300 bg-danger-50 text-danger-800'
                }`}
              >
                <code>{item.postId}</code>:{' '}
                {item.ok ? `OK (status=${item.status ?? '-'})` : `NG ${item.error ?? ''}`}
              </li>
            ))}
          </ul>
        </section>
      )}
    </Card>
  );
}
